import { useState, useRef, useCallback, type DragEvent, type ChangeEvent } from 'react';
import { Upload, Camera, X, FileText, ImageIcon, Loader2 } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import { usePresignUpload, useUploadToS3 } from '../../hooks/useUpload';
import { toast } from '../ui/Toast';
import {
  type UploadContext,
  COMPRESSION_THRESHOLD,
  MAX_IMAGE_SIZE,
  MAX_PDF_SIZE,
  getAllowedMimeTypes,
} from '@shared/validation/upload-schemas';

export interface UploadedFile {
  s3_key: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  preview_url?: string;
}

interface FileUploadProps {
  context: UploadContext;
  onFilesChange: (files: UploadedFile[]) => void;
  existingFiles?: UploadedFile[];
  maxFiles?: number;
  required?: boolean;
  label?: string;
  helpText?: string;
  entityId?: string;
}

export function FileUpload({
  context,
  onFilesChange,
  existingFiles = [],
  maxFiles = 10,
  required = false,
  label = 'Archivos',
  helpText,
  entityId,
}: FileUploadProps) {
  const [files, setFiles] = useState<UploadedFile[]>(existingFiles);
  const [uploading, setUploading] = useState<Map<string, number>>(new Map());
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // toast() is a global function from Toast.tsx

  const presign = usePresignUpload();
  const uploadToS3 = useUploadToS3();

  const allowedMimes = getAllowedMimeTypes(context);
  const acceptImages = allowedMimes.some((m) => m.startsWith('image/'));
  const acceptPdf = allowedMimes.includes('application/pdf');

  const acceptAttr = allowedMimes.join(',');
  const maxSize = acceptPdf ? MAX_PDF_SIZE : MAX_IMAGE_SIZE;
  const maxSizeMB = maxSize / (1024 * 1024);

  const processFile = useCallback(
    async (file: File) => {
      // Validate MIME type
      if (!allowedMimes.includes(file.type as (typeof allowedMimes)[number])) {
        toast('error', `Tipo no permitido: ${file.type}. Use: ${allowedMimes.join(', ')}`);
        return;
      }

      // Validate size
      if (file.size > maxSize) {
        toast('error', `Archivo demasiado grande. Máximo ${maxSizeMB}MB`);
        return;
      }

      // Check max files
      if (files.length >= maxFiles) {
        toast('warning', `Máximo ${maxFiles} archivos permitidos`);
        return;
      }

      const tempId = `uploading-${Date.now()}-${file.name}`;
      setUploading((prev) => new Map(prev).set(tempId, 0));

      try {
        // Compress images > 5MB
        let fileToUpload: File | Blob = file;
        if (file.type.startsWith('image/') && file.size > COMPRESSION_THRESHOLD) {
          fileToUpload = await imageCompression(file, {
            maxSizeMB: 4,
            maxWidthOrHeight: 2048,
            useWebWorker: true,
          });
        }

        // Get presigned URL
        setUploading((prev) => new Map(prev).set(tempId, 30));
        const presignResult = await presign.mutateAsync({
          filename: file.name,
          mime_type: file.type as 'image/jpeg' | 'image/png' | 'image/heic' | 'application/pdf',
          context,
          entity_id: entityId,
        });

        // Upload to S3
        setUploading((prev) => new Map(prev).set(tempId, 60));
        await uploadToS3.mutateAsync({
          uploadUrl: presignResult.upload_url,
          file: fileToUpload,
          contentType: file.type,
        });

        setUploading((prev) => new Map(prev).set(tempId, 100));

        // Create preview URL for images
        const previewUrl = file.type.startsWith('image/')
          ? URL.createObjectURL(file)
          : undefined;

        const uploadedFile: UploadedFile = {
          s3_key: presignResult.s3_key,
          filename: file.name,
          mime_type: file.type,
          size_bytes: fileToUpload instanceof File ? fileToUpload.size : (fileToUpload as Blob).size,
          preview_url: previewUrl,
        };

        setFiles((prev) => {
          const updated = [...prev, uploadedFile];
          onFilesChange(updated);
          return updated;
        });

        toast('success', 'Archivo subido exitosamente');
      } catch {
        toast('error', `Error al subir ${file.name}`);
      } finally {
        setUploading((prev) => {
          const next = new Map(prev);
          next.delete(tempId);
          return next;
        });
      }
    },
    [files, maxFiles, maxSize, maxSizeMB, allowedMimes, context, entityId, presign, uploadToS3, onFilesChange],
  );

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList) return;
      Array.from(fileList).forEach(processFile);
    },
    [processFile],
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      if (inputRef.current) inputRef.current.value = '';
    },
    [handleFiles],
  );

  const removeFile = useCallback(
    (s3Key: string) => {
      setFiles((prev) => {
        const file = prev.find((f) => f.s3_key === s3Key);
        if (file?.preview_url) URL.revokeObjectURL(file.preview_url);
        const updated = prev.filter((f) => f.s3_key !== s3Key);
        onFilesChange(updated);
        return updated;
      });
    },
    [onFilesChange],
  );

  const isUploading = uploading.size > 0;

  return (
    <div className="space-y-3">
      {/* Label */}
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>

      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative rounded-xl border-2 border-dashed p-6 text-center transition-colors
          ${isDragging ? 'border-brand-blue bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
          ${isUploading ? 'pointer-events-none opacity-60' : 'cursor-pointer'}
        `}
        onClick={() => !isUploading && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept={acceptAttr}
          multiple={maxFiles > 1}
          onChange={handleInputChange}
          className="hidden"
        />

        <div className="flex flex-col items-center gap-2">
          <Upload className="h-8 w-8 text-gray-400" />
          <p className="text-sm text-gray-600">
            Arrastra archivos aquí o <span className="text-brand-blue font-medium">selecciona</span>
          </p>
          <p className="text-xs text-gray-400">
            {acceptPdf ? 'JPG, PNG, HEIC, PDF' : 'JPG, PNG, HEIC'} &middot; Máx {maxSizeMB}MB
          </p>
        </div>

        {/* Mobile camera button */}
        {acceptImages && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              const cameraInput = document.createElement('input');
              cameraInput.type = 'file';
              cameraInput.accept = 'image/*';
              cameraInput.capture = 'environment';
              cameraInput.onchange = (ev) => {
                const target = ev.target as HTMLInputElement;
                handleFiles(target.files);
              };
              cameraInput.click();
            }}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-200 md:hidden"
          >
            <Camera size={14} />
            Usar cámara
          </button>
        )}
      </div>

      {/* Upload progress */}
      {uploading.size > 0 && (
        <div className="space-y-2">
          {Array.from(uploading.entries()).map(([id, progress]) => (
            <div key={id} className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-brand-blue" />
              <div className="flex-1">
                <div className="h-1.5 rounded-full bg-gray-200">
                  <div
                    className="h-1.5 rounded-full bg-brand-blue transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
              <span className="text-xs text-gray-500">{progress}%</span>
            </div>
          ))}
        </div>
      )}

      {/* Thumbnail previews */}
      {files.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {files.map((file) => (
            <div
              key={file.s3_key}
              className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white"
            >
              {file.preview_url ? (
                <img
                  src={file.preview_url}
                  alt={file.filename}
                  className="aspect-square w-full object-cover"
                />
              ) : (
                <div className="flex aspect-square items-center justify-center bg-gray-50">
                  {file.mime_type === 'application/pdf' ? (
                    <FileText className="h-8 w-8 text-red-400" />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-gray-400" />
                  )}
                </div>
              )}

              {/* Filename overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
                <p className="truncate text-xs text-white">{file.filename}</p>
              </div>

              {/* Delete button */}
              <button
                type="button"
                onClick={() => removeFile(file.s3_key)}
                className="absolute right-1 top-1 rounded-full bg-red-500 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Help text */}
      {helpText && <p className="text-xs text-gray-500">{helpText}</p>}
    </div>
  );
}
